import React from 'react';
import { Card, Typography, Skeleton, Empty } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useAppSelector } from '../../hooks/redux';
import { useGetDashboardPollingQuery } from '../../services/analyticsApi';
import { DASHBOARD_QUERY, DASHBOARD_POLLING_MS } from './dashboardQuery';
import { useMoneyPrivacy } from '../../hooks/useMoneyPrivacy';
import MoneyEye from './MoneyEye';

const { Text } = Typography;

// Color fijo por método (nunca por posición) para que un método conserve su
// color aunque otro desaparezca del desglose.
const METHOD_COLORS: Record<string, string> = {
  cash: 'var(--color-success)',
  card: 'var(--color-info)',
  credit_card: 'var(--color-info)',
  debit_card: 'var(--color-cyan)',
  transfer: 'var(--color-reserved)',
  other: 'var(--color-neutral-text)',
};

const PaymentMethodsCard: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { mask } = useMoneyPrivacy();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);

  const { data: dashboard, isLoading } = useGetDashboardPollingQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      query: DASHBOARD_QUERY,
    },
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: DASHBOARD_POLLING_MS,
    }
  );

  if (!currentRestaurant?.id) {
    return null;
  }

  const breakdown = dashboard?.realTimeMetrics.paymentMethodBreakdown ?? [];
  const totalAmount = breakdown.reduce((sum, m) => sum + m.amount, 0);
  const totalCount = breakdown.reduce((sum, m) => sum + m.count, 0);

  const formatMoney = (value: number) =>
    `${currencySymbol} ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CreditCardOutlined style={{ marginRight: 8, color: 'var(--color-info-text)' }} />
          <span>Métodos de pago</span>
        </div>
      }
      extra={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Text type="secondary">Hoy</Text>
          <MoneyEye moneyKey="payments" />
        </span>
      }
      style={{ borderRadius: isMobile ? 8 : 12, height: '100%' }}
    >
      {isLoading || !dashboard ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : breakdown.length === 0 ? (
        <Empty description="Sin cobros hoy" style={{ margin: '24px 0' }} />
      ) : (
        <>
          {breakdown.map((method) => {
            const color = METHOD_COLORS[method.method] ?? METHOD_COLORS.other;
            return (
              <div
                key={method.method}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: '1px solid var(--border-color)',
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 3,
                    backgroundColor: color,
                    flex: 'none',
                  }}
                />
                <Text style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 600, width: isMobile ? 90 : 110 }}>
                  {method.displayName}
                </Text>
                <div
                  style={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'var(--bg-tertiary)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, method.percentage)}%`,
                      height: '100%',
                      borderRadius: 4,
                      backgroundColor: color,
                    }}
                  />
                </div>
                <div style={{ textAlign: 'right', minWidth: isMobile ? 86 : 104 }}>
                  <Text strong style={{ fontSize: isMobile ? '12px' : '13px' }}>
                    {mask('payments', formatMoney(method.amount))}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: 4 }}>
                    {method.percentage.toFixed(0)}%
                  </Text>
                </div>
              </div>
            );
          })}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 12,
              paddingTop: 10,
            }}
          >
            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
              {totalCount} {totalCount === 1 ? 'cobro' : 'cobros'}
            </Text>
            <Text strong style={{ fontSize: isMobile ? '12px' : '13px' }}>
              {mask('payments', formatMoney(totalAmount))}
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

export default PaymentMethodsCard;
